/**
 * The photo hand-off by QR code (Phase 2 W5-6; plan PHASE_2_W5.md §8; owner points 4, 9, 10; Q3): a
 * page — the admin web, or Dob Desktop with or without internet — shows a code; Dob Staff reads it
 * with its own camera scanner, takes the photo(s) and uploads them at once, straight to the server
 * and storage. The page sees them arrive and the document it saves names the hand-off, so the server
 * links its stored photos to that document.
 *
 * - The page makes the code itself (no server call): a hand-off id (UUIDv7), a 32-byte random secret,
 *   the purpose (INVOICE on the invoice form, DAMAGE on the receipt form), the location and its time.
 * - The code is plain text (`DOB1:…`), **not a web link**: a phone's own camera app cannot send it to
 *   a browser.
 * - The server keeps only the secret's SHA-256. Whoever holds the secret (the page) may see the
 *   hand-off's photos and use them, once, for one document.
 * - The photos are never kept on the phone (PD13): uploaded while the person waits, or not at all.
 */
import { z } from 'zod';

import { isUuidV7, newId } from '../ids/ids.ts';
import { AttachmentDeclareSchema } from './attachments.ts';

export const HANDOFF_CODE_PREFIX = 'DOB1:';
/** What a hand-off may carry: an invoice's paper, or damaged goods at a receipt. */
export const HANDOFF_PURPOSES = ['INVOICE', 'DAMAGE'] as const;
export type HandoffPurpose = (typeof HANDOFF_PURPOSES)[number];
/** A code older than this is refused (the page shows a new one). */
export const HANDOFF_MAX_AGE_MS = 10 * 60_000;
/** A page's clock may run a little ahead of the server's. */
export const HANDOFF_CLOCK_AHEAD_MS = 2 * 60_000;
/** Photos in one hand-off (a long receipt in parts is fine). */
export const HANDOFF_PHOTOS_MAX = 10;
/** How often an online page asks whether photos arrived. */
export const HANDOFF_POLL_MS = 3_000;

const SECRET = /^[A-Za-z0-9_-]{43}$/;
const v7 = z
  .uuid()
  .refine(isUuidV7, { message: 'UUIDv7 expected', params: { dobCode: 'invalid_id' } });

/** 32 random bytes, base64url without padding (43 characters). */
export const handoffSecretSchema = z.string().regex(SECRET);

/** How a document names its hand-off when it is saved: the server checks the secret. */
export const PhotoHandoffRefSchema = z.strictObject({
  id: v7,
  secret: handoffSecretSchema,
});
export type PhotoHandoffRef = z.infer<typeof PhotoHandoffRefSchema>;

export interface HandoffCode {
  readonly id: string;
  readonly secret: string;
  readonly purpose: HandoffPurpose;
  readonly locationId: string;
  /** When the page made it (ms since 1970). */
  readonly issuedAt: number;
}

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/** Bytes as base64url without padding (no Buffer or btoa: the same on the server, a page and a phone). */
export function base64Url(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i] ?? 0;
    const b = bytes[i + 1] ?? 0;
    const c = bytes[i + 2] ?? 0;
    const n = (a << 16) | (b << 8) | c;
    out += ALPHABET[(n >> 18) & 63] ?? '';
    out += ALPHABET[(n >> 12) & 63] ?? '';
    if (i + 1 < bytes.length) out += ALPHABET[(n >> 6) & 63] ?? '';
    if (i + 2 < bytes.length) out += ALPHABET[n & 63] ?? '';
  }
  return out;
}

/** A new code, made by the page. `randomBytes` is the platform's secure random (Web Crypto). */
export function newHandoffCode(
  purpose: HandoffPurpose,
  locationId: string,
  now: number,
  randomBytes: (length: number) => Uint8Array,
): HandoffCode {
  const random = randomBytes(32);
  if (random.length !== 32) throw new Error('32 random bytes expected');
  return { id: newId(), secret: base64Url(random), purpose, locationId, issuedAt: now };
}

/** The text the QR holds: `DOB1:<id>.<secret>.<purpose>.<location>.<time in seconds>`. */
export function handoffCodeText(code: HandoffCode): string {
  return `${HANDOFF_CODE_PREFIX}${code.id}.${code.secret}.${code.purpose}.${code.locationId}.${String(
    Math.floor(code.issuedAt / 1000),
  )}`;
}

/** A code read by the phone, or null when the text is not one of ours (any other QR). */
export function parseHandoffCode(text: string): HandoffCode | null {
  const trimmed = text.trim();
  if (!trimmed.startsWith(HANDOFF_CODE_PREFIX)) return null;
  const parts = trimmed.slice(HANDOFF_CODE_PREFIX.length).split('.');
  if (parts.length !== 5) return null;
  const [id = '', secret = '', purpose = '', locationId = '', seconds = ''] = parts;
  const parsed = z
    .strictObject({
      id: v7,
      secret: handoffSecretSchema,
      purpose: z.enum(HANDOFF_PURPOSES),
      locationId: z.uuid(),
      seconds: z.string().regex(/^[0-9]{9,11}$/),
    })
    .safeParse({ id, secret, purpose, locationId, seconds });
  if (!parsed.success) return null;
  return {
    id: parsed.data.id,
    secret: parsed.data.secret,
    purpose: parsed.data.purpose,
    locationId: parsed.data.locationId,
    issuedAt: Number(parsed.data.seconds) * 1000,
  };
}

/** Whether a code may still be used at `now` (by the server's clock; also shown by the phone). */
export function handoffCodeAge(code: Pick<HandoffCode, 'issuedAt'>, now: number): 'ok' | 'old' {
  return now - code.issuedAt <= HANDOFF_MAX_AGE_MS && code.issuedAt - now <= HANDOFF_CLOCK_AHEAD_MS
    ? 'ok'
    : 'old';
}

/** One photo the phone uploads for a hand-off: the code it read and the photo's declaration. */
export const HandoffUploadSchema = z.strictObject({
  handoffId: v7,
  secret: handoffSecretSchema,
  purpose: z.enum(HANDOFF_PURPOSES),
  locationId: z.uuid(),
  issuedAt: z.number().int().min(0),
  photo: AttachmentDeclareSchema.omit({ purpose: true }),
});
export type HandoffUpload = z.infer<typeof HandoffUploadSchema>;

/** The page asks what arrived (the secret in the body, never in the address). */
export const HandoffStatusRequestSchema = z.strictObject({ secret: handoffSecretSchema });

/** What the page sees (it made the code: it knows the purpose and the place). */
export interface HandoffStatus {
  readonly id: string;
  /** In the order the phone sent them; DECLARED until its bytes are stored. */
  readonly photos: readonly { readonly id: string; readonly state: 'DECLARED' | 'STORED' }[];
  /** Already linked to a document (a hand-off serves one document). */
  readonly used: boolean;
}
